import i18n from "../config/i18n";

/**
 * Format a timestamp into a human-readable date label
 * @param timestamp - The timestamp to format (Date object, ISO string, or Unix timestamp)
 * @returns Formatted date string based on relative time (today, yesterday, this year, or full date)
 */
export const formatDateLabel = (timestamp: string | Date | number): string => {
  // Create date objects once to avoid multiple instantiations
  const date = new Date(timestamp);
  const now = new Date();

  // Get date strings once for comparison
  const dateString = date.toDateString();
  const nowString = now.toDateString();

  // Create yesterday date once
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const yesterdayString = yesterday.toDateString();

  // Check time relationships
  const isToday = dateString === nowString;
  const isYesterday = dateString === yesterdayString;
  const isCurrentYear = date.getFullYear() === now.getFullYear();

  // Get current language with fallback to 'en'
  const language = i18n.language || "en";

  // Return appropriate format based on date
  if (isToday) {
    return i18n.t("dates.today", { defaultValue: "Today" });
  } else if (isYesterday) {
    return i18n.t("dates.yesterday", { defaultValue: "Yesterday" });
  } else if (isCurrentYear) {
    // Month and day for current year
    return date.toLocaleDateString(language, {
      month: "long",
      day: "numeric",
    });
  } else {
    // Full date for other years
    return date.toLocaleDateString(language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
};

/**
 * Format a timestamp into a month-year label
 * @param timestamp - The timestamp to format (Date object, ISO string, or Unix timestamp)
 * @returns Formatted month-year string (e.g., "July 2025")
 */
export const formatMonthLabel = (timestamp: string | Date | number): string => {
  // Create date object
  const date = new Date(timestamp);
  const now = new Date();

  // Get current language with fallback to 'en'
  const language = i18n.language || "en";

  // Check if it's the current year
  const isCurrentYear = date.getFullYear() === now.getFullYear();
  if (isCurrentYear) {
    // Just show month for current year
    return date.toLocaleDateString(language, {
      month: "long",
    });
  } else {
    // Show month and year for other years
    return date.toLocaleDateString(language, {
      year: "numeric",
      month: "long",
    });
  }
};

/**
 * Format a timestamp into a relative time string (e.g., "2 hours ago")
 * @param timestamp - The timestamp to format (Date object, ISO string, or Unix timestamp)
 * @returns Formatted relative time string
 */
export const formatTimeAgo = (timestamp: string | Date | number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Get current language with fallback to 'en'
  const language = i18n.language || "en";

  // Define time intervals
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  // Use Intl.RelativeTimeFormat if available
  if (typeof Intl !== "undefined" && Intl.RelativeTimeFormat) {
    const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });

    if (seconds < intervals.minute) {
      return rtf.format(-Math.floor(seconds), "second");
    } else if (seconds < intervals.hour) {
      return rtf.format(-Math.floor(seconds / intervals.minute), "minute");
    } else if (seconds < intervals.day) {
      return rtf.format(-Math.floor(seconds / intervals.hour), "hour");
    } else if (seconds < intervals.week) {
      return rtf.format(-Math.floor(seconds / intervals.day), "day");
    } else if (seconds < intervals.month) {
      return rtf.format(-Math.floor(seconds / intervals.week), "week");
    } else if (seconds < intervals.year) {
      return rtf.format(-Math.floor(seconds / intervals.month), "month");
    } else {
      return rtf.format(-Math.floor(seconds / intervals.year), "year");
    }
  }

  // Fallback for older browsers
  if (seconds < intervals.minute) {
    return i18n.t("time.just_now", { defaultValue: "Just now" });
  } else if (seconds < intervals.hour) {
    const minutes = Math.floor(seconds / intervals.minute);
    return i18n.t("time.minutes_ago", {
      count: minutes,
      defaultValue: "{{count}} minutes ago",
    });
  } else if (seconds < intervals.day) {
    const hours = Math.floor(seconds / intervals.hour);
    return i18n.t("time.hours_ago", {
      count: hours,
      defaultValue: "{{count}} hours ago",
    });
  } else if (seconds < intervals.week) {
    const days = Math.floor(seconds / intervals.day);
    return i18n.t("time.days_ago", {
      count: days,
      defaultValue: "{{count}} days ago",
    });
  } else {
    // For older dates, use the standard date format
    return formatDateLabel(timestamp);
  }
};
